import { Component, OnInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { SharedService } from '@shared/service/sharedservice';
import { ApolloUserService } from '@core/graphql/apolloUser.service';
import { ApolloStudentService } from '@core/graphql/apolloStudent.service';
import { ApolloSchoolService } from '@core/graphql/apolloSchool.service';
import { ApolloFeeService } from '@core/graphql/apolloFee.service';
import { DatePipeService } from '@core/pipe/datepipe.service';
import { of } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { ConstantsService } from '@shared/service/constants.services';
import { jwtDecode } from 'jwt-decode';

@Component({
  selector: 'app-parent-sidebar',
  templateUrl: './parent-sidebar.component.html',
  styleUrl: './parent-sidebar.component.scss'
})
export class ParentSidebarComponent implements OnInit {
  isCardVisible: boolean = false;
  activeKid: any = null;
  years: number[] = [];
  selectedYear: number = new Date().getFullYear();
  isMobile: boolean = window.innerWidth <= 767;

  userId: any;
  userDetail: any;
  rolesString: string = '';
  showAlert: boolean = false;
  alertProperties = { type: '', message: '' };

  studentsList: any[] = [];
  paymentHistoryList: any[] = [];
  paymentList: any[] = [];
  kids: any[] = [];
  schoolYearList: any[] = [];
  schoolList: string[] = [];
  profileImageBase64: string = '';

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.isMobile = window.innerWidth <= 767;
  }

  constructor(private router: Router, private sharedService: SharedService, private userService: ApolloUserService, private studentService: ApolloStudentService,
    private schoolService: ApolloSchoolService, private feeService: ApolloFeeService, private datePipeService: DatePipeService, private cdr: ChangeDetectorRef, private constantService: ConstantsService,
  ) { }

  ngOnInit(): void {
    this.fetchStudents(this.selectedYear);

    this.sharedService.cardVisibility$.subscribe((isVisible) => {
      this.isCardVisible = isVisible;
    });

    this.sharedService.selectedKid$.subscribe((kid) => {
      if (kid) {
        this.activeKid = kid;
      }
    });

    this.sharedService.setSelectedYear(this.selectedYear);
  }

  fetchStudents(selectedYear: number): void {
    const userProfile = localStorage.getItem("userProfile");
    if (!userProfile) {
      this.router.navigate(['/login']);
      return;
    }

    const parsedProfile = JSON.parse(userProfile);
    this.userId = parsedProfile.id;

    if (this.userId) {
      this.getUserById(this.userId);
      this.years = [];
      this.studentsList = [];

      this.studentService.getStudentByParentId(this.userId).pipe(
        tap(students => console.log("Students API Response:", students)),
        catchError(error => {
          console.error("Error fetching students:", error);
          return of([]);
        }),
        finalize(() => console.log("Students API Completed"))
      ).subscribe(students => {
        let resolvedYear = Number(selectedYear);
        if (!students.length) {
          this.years = [];
          this.studentsList = [];
          return;
        }
        this.years = [...new Set(
          students
            .map(student => Number(student.schoolYear?.year))
            .filter(year => Number.isFinite(year))
        )].sort((a, b) => b - a);

        if (this.years.length && !this.years.includes(resolvedYear)) {
          resolvedYear = this.years[0];
          this.selectedYear = resolvedYear;
          this.sharedService.setSelectedYear(this.selectedYear);
        }

        this.studentsList = students.filter(student => student.schoolYear?.year === resolvedYear);
        this.processStudentData();
        this.fetchPaymentsForAllStudents();
      });
    }
  }

  fetchPayments(selectedKidId: any): Promise<void> {
    return new Promise((resolve) => {
      this.feeService.getPaymentHistory(selectedKidId, this.selectedYear).pipe(
        tap(payments => console.log("Payments API Response:", payments)),
        catchError(error => {
          console.error("Error fetching payments:", error);
          return of([]);
        }),
        finalize(() => console.log("Payments API Completed"))
      ).subscribe(payments => {
        this.paymentHistoryList = payments || [];
        this.processStudentData();
        resolve();
      });
    });
  }

  fetchPaymentsForAllStudents(): void {
    if (!this.studentsList || this.studentsList.length === 0) {
      this.paymentList = [];
      return;
    }
  
    this.paymentList = [];
    let pendingRequests = this.studentsList.length;
  
    this.studentsList.forEach((student) => {
      this.feeService.getPaymentHistory(student.id, this.selectedYear).pipe(
        catchError(error => {
          console.error(`Error fetching payments for Student ID ${student.id}:`, error);
          return of([]);
        })
      ).subscribe(payments => {
        const mappedPayments = payments.map((payment: any) => ({
          id: payment.id,
          studentName: `${student.firstName} ${student.lastName}`,
          amount: this.calculateUnpaidAmount(payment),
          feeName: this.constantService.calendar.monthOption
            .filter((i: any) => payment.month === i.value)
            .map(m => `${m.name} ${payment.year}`),
          status: payment.refStatus?.name || "Unknown",
          month: payment.month
        }));

        this.paymentList = [...this.paymentList, ...mappedPayments];
      });
    });
  }  

  processStudentData(): void {
    if (!this.studentsList || this.studentsList.length === 0) {
      this.displayAlert("danger", "No students found.");
      this.kids = [];
      return;
    }

    this.kids = this.studentsList
      .filter(student => student.schoolYear?.year.toString() === this.selectedYear.toString())
      .map((student: any) => ({
        id: student.id,
        name: `${student.firstName} ${student.lastName}`,
        grade: student.class?.grade?.name || "N/A",
        class: student.class?.name || "N/A",
        gender: student.gender || "N/A",
        myKidNo: student.myKidNo || "N/A",
        birthCertificateNo: student.birthCertificateNo || "N/A",
        schoolId: student.schoolYear?.school?.id || "",
        school: student.schoolYear?.school?.name || "N/A",
        schoolDesc: student.schoolYear?.school?.description || "N/A",
        dateOfBirth: this.datePipeService.convertDateTime(student.dateOfBirth, "dd MMM YYYY"),
        yearRegister: student.schoolYear?.year ? student.schoolYear.year.toString() : "N/A",
        exams: [],
        paymentData: student.masterPayment?.fees?.map((fee: any) => ({
          id: fee.id,
          name: fee.name,
          amount: fee.amount,
          isMandatory: fee.isMandatory,
          discountType: fee.discountType ? [fee.discountType].map((disc: any) => ({
            name: disc.name,
            type: disc.type,
            value: disc.value
          })) : [],
          year: student.masterPayment?.year,
        })) || [],

        paymentHistory: this.paymentHistoryList
          .map((payment: any) => ({
            id: payment.id,
            amount: this.calculatePaymentAmount(payment),
            paymentMethod: payment.paymentMethod,
            feeName: this.constantService.calendar.monthOption.filter((i: any) => { return payment.month === i.value }).map(m => { return m.name + " " + payment.year }),
            feeAmount: parseFloat(payment.fees?.amount || 0).toFixed(2),
            discountType: payment.fees?.discountType?.name || "None",
            status: payment.refStatus?.name || "Unknown",
            month: payment.month,
            paymentDetails: payment.paymentDetails,
            totalAmount: payment.totalAmount
          }))
          .sort((a, b) => a.month - b.month),
        avatar: "assets/images/empty-state.png",
        url: "/parent/mykids",
      }));
  }

  getUserById(id: any) {
    this.userService.getUserById(id).subscribe({
      next: (result: any) => {
        this.userDetail = result;
        this.rolesString = this.userDetail?.userRoles
          ?.map((role: { refRole: { name: string } }) => role.refRole.name)
          .join(', ') || '';

        if (result.profilePictureBase64) {
          this.profileImageBase64 = `data:image/png;base64,${result.profilePictureBase64}`;
        } else {
          this.profileImageBase64 = 'assets/images/user-profile.jpg';
        }
      },
      error: () => {
        this.displayAlert("danger", "Failed to Fetch Data");
      }
    });
  }

  getSchoolYearByParentId() {
    this.schoolService.getSchoolYearByParentId(this.userId).subscribe({
      next: (result: any) => {
        if (!result || !Array.isArray(result)) {
          this.displayAlert("danger", "Invalid school year data.");
          return;
        }
        this.years = result
          .map((sy: any) => Number(sy.year))
          .filter((year: number) => Number.isFinite(year))
          .sort((a, b) => b - a);
        if (!this.years.includes(this.selectedYear)) {
          this.selectedYear = this.years.length ? this.years[0] : new Date().getFullYear();
          this.sharedService.setSelectedYear(this.selectedYear);
        }
        this.schoolList = result.filter((sy: any) => sy.year === this.selectedYear)
          .map((sy: any) => sy.school?.name || "No School");

        this.sharedService.setSchoolList(this.schoolList);
      },
      error: () => {
        this.displayAlert("danger", "Failed to fetch school year list.");
      }
    });
  }

  onKidNameClick(kid: any): void {
    this.fetchPayments(kid.id).then(() => {
      const selectedKid = this.kids.find(k => k.id === kid.id);

      if (!selectedKid) {
        this.displayAlert("danger", "Selected kid not found in this.kids.");
        return;
      }
      this.activeKid = {
        ...selectedKid,
        paymentHistory: selectedKid.paymentHistory
      };

      this.sharedService.setSelectedKid(this.activeKid);
      this.sharedService.setPaymentList(this.paymentList);
      this.sharedService.showCard(true);
      this.router.navigate(['/parent/mykids']);
    });
  }

  onYearChange(): void {
    const previousYear = this.selectedYear;
    this.selectedYear = Number(this.selectedYear);
    const userInput = { year: this.selectedYear };

    this.schoolService.updateSchoolYearClaim(userInput).subscribe({
      next: (response) => {
        if (response?.success) {
          const decodedToken = jwtDecode(response.success);
          localStorage.setItem("userClaim", JSON.stringify(decodedToken));
          localStorage.setItem("userSecret", response.success);
          this.sharedService.setSelectedYear(this.selectedYear);
          this.fetchStudents(this.selectedYear);
          this.getSchoolYearByParentId();
          this.sharedService.setSelectedKid(null);
          return;
        }

        this.displayAlert("danger", response?.error || "Failed to Change School Year");
        this.selectedYear = previousYear;
      },
      error: () => {
        this.displayAlert("danger", "Failed to Change School Year");
        this.selectedYear = previousYear;
      }
    });
  }

  displayAlert(type: string, message: string) {
    this.alertProperties = { type, message };
    this.showAlert = true;
    setTimeout(() => this.showAlert = false, 5000);
  }

  private calculatePaymentAmount(payment: any): string {
    const details = payment?.paymentDetails;
    if (Array.isArray(details) && details.length) {
      const total = details.reduce((sum: number, detail: any) => {
        const amount = detail?.amount ?? detail?.fees?.amount ?? 0;
        return sum + Number(amount || 0);
      }, 0);
      return total.toFixed(2);
    }
    const totalAmount = Number(payment?.totalAmount);
    if (!Number.isNaN(totalAmount) && totalAmount > 0) {
      return totalAmount.toFixed(2);
    }
    return '0.00';
  }

  private calculateUnpaidAmount(payment: any): string {
    const details = payment?.paymentDetails;
    if (Array.isArray(details) && details.length) {
      const unpaidTotal = details
        .filter((detail: any) => (detail?.refStatus?.name || '').toLowerCase() !== 'paid')
        .reduce((sum: number, detail: any) => {
          const amount = detail?.amount ?? detail?.fees?.amount ?? 0;
          return sum + Number(amount || 0);
        }, 0);
      return unpaidTotal.toFixed(2);
    }
    const totalAmount = Number(payment?.totalAmount);
    if (!Number.isNaN(totalAmount) && totalAmount > 0) {
      return totalAmount.toFixed(2);
    }
    return '0.00';
  }
}
